/**
 * German (de) translations.
 *
 * Same key shape as the Slovak source dictionary (`Dictionary` = `typeof sk`).
 * TypeScript flags any missing or extra key. Brand name "Solandio" stays
 * unchanged (indeclinable) in all locales.
 *
 * DUAL-MODE COPY: Solandio is an intact-family app by default ('family' mode);
 * the co-parenting machinery sleeps behind `family.mode === 'separated'`. Warm,
 * plain-German, second-person tone in family mode; legal vocabulary
 * (`Gericht`, `Wechselmodell`, `Übergabe`, `50/50`) only in the explicit
 * `*Separated` strings.
 */
import { type Dictionary } from './sk';

export const de: Dictionary = {
  appName: 'Solandio',
  tagline: 'Ein sonniger Ort für Ihre Kinder und Ihre Familie.',

  language: {
    label: 'Sprache',
    select: 'Sprache wählen',
  },

  common: {
    save: 'Speichern',
    saving: 'Wird gespeichert…',
    cancel: 'Abbrechen',
    add: 'Hinzufügen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    remove: 'Entfernen',
    close: 'Schließen',
    loading: 'Wird geladen…',
    none: 'Keine',
    optional: 'optional',
    back: 'Zurück',
    today: 'Heute',
    note: 'Notiz',
    required: 'Dieses Feld ist erforderlich.',
    error: 'Etwas ist schiefgelaufen.',
    retry: 'Erneut versuchen',
    confirmDelete: 'Wirklich entfernen?',
    skipToContent: 'Zum Inhalt springen',
  },

  auth: {
    signInTitle: 'Willkommen bei Solandio',
    signInLead:
      'Ein ruhiger, übersichtlicher Ort, an dem die ganze Familie den Überblick behält — Kalender, Kinder, Einkäufe und Kosten.',
    signInMicrosoft: 'Anmelden oder registrieren',
    signInGoogle: 'Mit Google anmelden',
    signInMock: 'Anmelden (lokale Entwicklung)',
    signOut: 'Abmelden',
    signedInAs: 'Angemeldet als',
    loadingSession: 'Anmeldung wird überprüft…',
    warmingUp:
      'Solandio wird vorbereitet… beim ersten Öffnen nach längerer Zeit kann das ein paar Sekunden dauern.',
    devNotice:
      'Lokaler Entwicklungsmodus – ohne Azure. Die Daten werden nur in Ihrem Browser gespeichert.',
  },

  nav: {
    home: 'Start',
    children: 'Kinder',
    contacts: 'Kontakte',
    health: 'Gesundheit',
    calendar: 'Kalender',
    shopping: 'Einkäufe',
    documents: 'Dokumente',
    messages: 'Nachrichten',
    expenses: 'Kosten',
    agreements: 'Vereinbarungen',
    sharing: 'Familie',
    audit: 'Protokoll',
    settings: 'Einstellungen',
    modules: 'Module',
    menu: 'Menü',
  },

  settings: {
    title: 'Einstellungen',
    lead: 'Verwalten Sie Ihr Profil, die Sprache und die Anmeldung.',
    profile: 'Profil',
    displayName: 'Anzeigename',
    displayNameHint: 'Diesen Namen sehen andere Familienmitglieder.',
    email: 'E-Mail',
    emailHint: 'Ihre E-Mail-Adresse kann nicht geändert werden – sie ist mit Ihrer Anmeldung verknüpft.',
    nameInvalid: 'Geben Sie einen Namen ein (1–120 Zeichen).',
    saved: 'Gespeichert.',
    preferences: 'Einstellungen',
    account: 'Konto',
    signOutHint: 'Von diesem Gerät abmelden.',
  },

  profileSetup: {
    title: 'Willkommen bei Solandio!',
    lead: 'Bevor wir beginnen, sagen Sie uns, wie wir Sie nennen sollen.',
    nameLabel: 'Wie sollen wir Sie nennen?',
    nameHint: 'Andere Familienmitglieder sehen diesen Namen. Sie können ihn jederzeit in den Einstellungen ändern.',
    namePlaceholder: 'z. B. Jana',
    nameInvalid: 'Bitte geben Sie einen Namen ein (1–120 Zeichen).',
    submit: 'Weiter',
  },

  family: {
    switcher: 'Familie',
    create: 'Familie erstellen',
    createTitle: 'Erstellen Sie Ihre Familie',
    createLead:
      'Die Familie ist Ihr gemeinsamer Raum — für den Partner, die Kinder und die Großeltern. Sie entscheiden, wer was sieht.',
    nameLabel: 'Familienname',
    namePlaceholder: 'z. B. Unsere Familie',
    empty: 'Sie haben noch keine Familie.',
  },

  home: {
    /** Greeting; {name} is the signed-in member's first name. */
    greetingMorning: 'Guten Morgen, {name}',
    greetingDay: 'Guten Tag, {name}',
    greetingEvening: 'Guten Abend, {name}',
    /** Fallback greeting when no name is known yet. */
    greetingNeutral: 'Willkommen zu Hause',
    add: 'Hinzufügen',
    /** Section titles on the at-a-glance dashboard. */
    todayTitle: 'Heute',
    childrenTitle: 'Kinder',
    shoppingTitle: 'Einkäufe',
    expensesTitle: 'Haushaltskosten',
    lastNoteTitle: 'Letzte Nachricht',
    /** Small links/summaries under sections. */
    tomorrow: 'Morgen',
    tomorrowEvents: 'Morgen: {count} Termine',
    viewAll: 'Alle anzeigen',
    nothingToday: 'Heute steht nichts im Kalender.',
    noChildrenYet: 'Hier ist noch kein Kind.',
    noShoppingYet: 'Keine Einkaufsliste.',
    noExpensesYet: 'Diesen Monat noch keine Kosten.',
    noNotesYet: 'Noch keine Nachrichten.',
    thisMonth: 'Diesen Monat',
    /** Expenses summary line, e.g. "3 nevyrovnané". */
    outstandingCount: '{count} offen',
    /** Empty state — brand-new family with nothing added yet. */
    emptyTitle: 'Willkommen zu Hause 👋',
    emptyLead: 'Drei Schritte und Ihre Familie ist an einem Ort.',
    emptyAddChild: 'Fügen Sie das erste Kind hinzu',
    emptyInvitePartner: 'Laden Sie den Partner ein',
    emptyCreateList: 'Erstellen Sie eine Einkaufsliste',
  },

  children: {
    title: 'Kinder',
    add: 'Kind hinzufügen',
    addTitle: 'Kind hinzufügen',
    none: 'Hier ist noch kein Kind.',
    noneLead: 'Fügen Sie das erste Kind hinzu — Geburtstag, Gesundheit und Kontakte haben Sie dann griffbereit.',
    nameLabel: 'Name',
    namePlaceholder: 'z. B. Ema',
    emojiLabel: 'Avatar (Emoji)',
    birthDateLabel: 'Geburtsdatum',
    ageYears: 'Jahre',
    selectPrompt: 'Wählen Sie oben ein Kind aus.',
    /** Kid-profile destination: labels for the per-child quick links. */
    profileTitle: 'Kinderprofil',
    profileLead: 'Alles über Ihr Kind an einem Ort — mit einem Klick.',
    quickHealth: 'Gesundheit',
    quickContacts: 'Kontakte',
    quickCalendar: 'Kalender',
    quickDocuments: 'Dokumente',
    birthday: 'Geburtstag',
    ageLabel: 'Alter',
    noBirthDate: 'Geburtsdatum ist nicht ausgefüllt.',
    editTitle: 'Kind bearbeiten',
    removeConfirm: 'Dieses Kind wirklich entfernen? Seine Einträge bleiben erhalten.',
  },

  contacts: {
    title: 'Kontakte',
    add: 'Kontakt hinzufügen',
    empty: 'Noch keine Kontakte.',
    typeLabel: 'Typ',
    labelLabel: 'Bezeichnung',
    labelPlaceholder: 'z. B. Dr. med. Nowak',
    phoneLabel: 'Telefon',
    addressLabel: 'Adresse',
    types: {
      doctor: 'Arzt',
      school: 'Schule',
      dentist: 'Zahnarzt',
      activity: 'AG / Verein',
      other: 'Sonstiges',
    },
  },

  health: {
    title: 'Gesundheitsakte',
    sensitive: 'Sensible Daten (DSGVO Art. 9)',
    lead: 'Wichtige Gesundheitsinformationen an einem Ort – für den Notfall.',
    allergies: 'Allergien',
    medications: 'Medikamente',
    insurance: 'Krankenkasse',
    bloodType: 'Blutgruppe',
    conditions: 'Diagnosen / Befunde',
    notes: 'Notizen',
    updatedAt: 'Zuletzt bearbeitet',
    placeholderNone: 'Nicht ausgefüllt',
  },

  calendar: {
    title: 'Kalender',
    add: 'Termin hinzufügen',
    empty: 'Noch keine Termine.',
    upcoming: 'Anstehend',
    titleLabel: 'Titel des Termins',
    typeLabel: 'Typ',
    startLabel: 'Beginn',
    endLabel: 'Ende',
    allDayLabel: 'Ganztägig',
    locationLabel: 'Ort',
    /** Separated-mode only: the custody sub-view tab label. */
    custodyView: 'Wechselmodell',
    /**
     * Event-type labels. `types` is the family-mode default (no handover/custody
     * surfaced). `typesSeparated` adds the co-parenting types and is selected
     * when `family.mode === 'separated'`. Both carry every key so callers can
     * index either map by the same `CalendarEventType`.
     */
    types: {
      appointment: 'Termin',
      handover: 'Sonstiges',
      custody: 'Sonstiges',
      school: 'Schule',
      activity: 'Aktivität',
      other: 'Sonstiges',
    },
    typesSeparated: {
      appointment: 'Termin',
      handover: 'Übergabe',
      custody: 'Wechsel',
      school: 'Schule',
      activity: 'Aktivität',
      other: 'Sonstiges',
    },
  },

  custody: {
    title: 'Wechselplan',
    none: 'Es ist noch kein Wechselplan eingerichtet.',
    patternLabel: 'Muster',
    startLabel: 'Planbeginn',
    notesLabel: 'Notizen',
    set: 'Plan einrichten',
    update: 'Plan bearbeiten',
    parentA: 'Elternteil A',
    parentB: 'Elternteil B',
    thisWeek: 'Diese Woche',
    patterns: {
      'week-week': 'Woche / Woche',
      '2-2-3': '2-2-3',
      custom: 'Eigenes',
    },
  },

  documents: {
    title: 'Dokumente',
    sensitive: 'Sensibel',
    add: 'Dokument hinzufügen',
    empty: 'Noch keine Dokumente.',
    nameLabel: 'Name',
    categoryLabel: 'Kategorie',
    fileLabel: 'Datei',
    fileHint: 'Im lokalen Modus wird die Datei nicht hochgeladen – nur die Metadaten werden gespeichert.',
    uploadedBy: 'Hochgeladen von',
    size: 'Größe',
  },

  shopping: {
    title: 'Einkäufe',
    /** List picker + creation. */
    listLabel: 'Liste',
    newList: 'Neue Liste',
    newListTitle: 'Neue Einkaufsliste',
    listNameLabel: 'Listenname',
    listNamePlaceholder: 'z. B. Lebensmittel',
    renameList: 'Liste umbenennen',
    deleteList: 'Liste löschen',
    deleteListConfirm: 'Diese Liste samt allen Einträgen wirklich löschen?',
    noLists: 'Noch keine Liste.',
    noListsLead: 'Erstellen Sie die erste Liste — zum Beispiel „Lebensmittel“ oder „Drogerie“.',
    /** Item add + list. */
    addItem: 'Eintrag hinzufügen',
    addItemPlaceholder: 'Eintrag hinzufügen…',
    itemNameLabel: 'Eintrag',
    empty: 'Die Liste ist leer.',
    emptyLead: 'Fügen Sie oben den ersten Eintrag hinzu.',
    /** Category grouping (free-form label on each item). */
    categoryLabel: 'Kategorie',
    categoryNone: 'Ohne Kategorie',
    categoryPlaceholder: 'z. B. Obst und Gemüse',
    /** Assignee = the member responsible for buying the item. */
    assigneeLabel: 'Wer kauft',
    assigneeNone: 'Egal wer',
    /** Buy-off. */
    bought: 'Gekauft',
    markBought: 'Als gekauft markieren',
    clearBought: 'Gekaufte entfernen',
    /** {count} → number of checked items to clear. */
    clearBoughtCount: 'Gekaufte entfernen ({count})',
    clearBoughtConfirm: 'Alle gekauften Einträge aus dieser Liste entfernen?',
    itemCount: '{count} Einträge',
  },

  messages: {
    /**
     * "Nachrichten" in family mode (warm) / "Nachrichtenjournal" in separated
     * (court-log). Bare `title`/`lead`/`empty` default to the family wording; the
     * `*Family` and `*Separated` variants let the mode-aware UI pick explicitly.
     * Under the hood messages stay append-only in BOTH modes.
     */
    title: 'Nachrichten',
    titleFamily: 'Nachrichten',
    titleSeparated: 'Nachrichtenjournal',
    lead: 'Kurze Familiennachrichten und Notizen — zum Beispiel „Hol Ema um 15:00 Uhr ab“.',
    leadFamily: 'Kurze Familiennachrichten und Notizen — zum Beispiel „Hol Ema um 15:00 Uhr ab“.',
    leadSeparated:
      'Ein unveränderbarer, gerichtlich verwendbarer Kommunikationsverlauf. Nachrichten lassen sich weder bearbeiten noch löschen.',
    placeholder: 'Nachricht schreiben…',
    placeholderSeparated: 'Nachricht schreiben…',
    send: 'Senden',
    empty: 'Noch keine Nachrichten. Schreiben Sie die erste.',
    emptyFamily: 'Noch keine Nachrichten. Schreiben Sie die erste.',
    emptySeparated: 'Das Journal ist noch leer. Schreiben Sie die erste Nachricht.',
    newest: 'Neueste',
    /** Shown only in separated mode (the append-only guarantee matters there). */
    appendOnlyNote: 'Nur anfügen – kein Bearbeiten und Löschen.',
  },

  expenses: {
    /**
     * "Haushaltskosten" (household budgeting) in family mode vs "Kosten"
     * with split/settle machinery in separated. Bare keys default to family
     * wording; `*Separated` variants carry the co-parenting framing.
     */
    title: 'Haushaltskosten',
    titleFamily: 'Haushaltskosten',
    titleSeparated: 'Kosten',
    lead: 'Den Überblick, wer was bezahlt hat — das gemeinsame Haushaltsbudget.',
    leadSeparated: 'Gemeinsame Kosten für die Kinder und ihre Aufteilung zwischen den Eltern.',
    add: 'Kosten hinzufügen',
    empty: 'Noch keine Kosten.',
    amountLabel: 'Betrag',
    categoryLabel: 'Kategorie',
    descriptionLabel: 'Beschreibung',
    childLabel: 'Kind',
    childAll: 'Ganze Familie',
    incurredOnLabel: 'Datum',
    /** Split UI is hidden in family mode; these labels surface in separated. */
    splitLabel: 'Aufteilung',
    split5050: 'Hälftig (50/50)',
    splitCustom: 'Eigene',
    payerShare: 'Ihr Anteil',
    otherShare: 'Anteil des anderen Elternteils',
    /** Family mode shows only "kto zaplatil" without any split. */
    paidBy: 'Bezahlt von',
    paidByFamily: 'Bezahlt von',
    settled: 'Ausgeglichen',
    outstanding: 'Offen',
    settle: 'Als ausgeglichen markieren',
    totalOutstanding: 'Offen gesamt',
    /** Family-mode month total (no settlement concept). */
    totalMonth: 'Gesamt diesen Monat',
    you: 'Sie',
  },

  agreements: {
    title: 'Vereinbarungen',
    lead:
      'Schriftliche Vereinbarungen zwischen den Eltern. Ein Elternteil schlägt eine Vereinbarung vor, der andere stimmt zu oder lehnt ab. Eine angenommene Vereinbarung ist ein unveränderbarer, gerichtlich verwendbarer Eintrag.',
    propose: 'Vereinbarung vorschlagen',
    proposeTitle: 'Vereinbarung vorschlagen',
    proposeLead:
      'Formulieren Sie die Vereinbarung klar. Der andere Elternteil kann ihr zustimmen oder sie ablehnen.',
    proposeNote:
      'Nach der Zustimmung wird die Vereinbarung gesperrt und lässt sich weder bearbeiten noch löschen.',
    titleLabel: 'Titel der Vereinbarung',
    titlePlaceholder: 'z. B. Sommerferien 2026',
    bodyLabel: 'Wortlaut der Vereinbarung',
    bodyPlaceholder: 'Beschreiben Sie, worauf Sie sich geeinigt haben…',
    empty: 'Noch keine Vereinbarungen.',
    emptyLead: 'Schlagen Sie dem anderen Elternteil die erste Vereinbarung vor.',
    proposedBy: 'Vorgeschlagen von',
    respondedBy: 'Bearbeitet von',
    accept: 'Zustimmen',
    decline: 'Ablehnen',
    withdraw: 'Zurückziehen',
    locked: 'Gesperrt – keine weiteren Änderungen.',
    actionStale: 'Die Vereinbarung wurde inzwischen bearbeitet. Wir haben die Liste aktualisiert.',
    you: 'Sie',
    status: {
      proposed: 'Vorgeschlagen',
      accepted: 'Angenommen',
      declined: 'Abgelehnt',
      withdrawn: 'Zurückgezogen',
    },
  },

  sharing: {
    title: 'Familie',
    lead:
      'Sehen Sie, wer zu Ihrer Familie gehört, und entscheiden Sie, was jede Person sieht. Großeltern können Sie als Vertrauensperson einladen – sie sehen nur, was Sie erlauben.',
    familyNameLabel: 'Familienname',
    rename: 'Umbenennen',
    renameTitle: 'Familie umbenennen',
    members: 'Familienmitglieder',
    invite: 'Einladen',
    inviteMember: 'Mitglied einladen',
    inviteTitle: 'Familienmitglied einladen',
    emailLabel: 'E-Mail',
    roleLabel: 'Rolle in der Familie',
    roleParent: 'Elternteil',
    roleParentDesc: 'Voller Zugriff auf alles – für den anderen Elternteil.',
    roleTrusted: 'Vertrauensperson',
    roleTrustedDesc:
      'Zum Beispiel Großeltern – sehen nur, was Sie ankreuzen.',
    scopesLabel: 'Was sie sehen können',
    scopesHint:
      'Ein Elternteil sieht alles. Eine Vertrauensperson sieht nur die angehakten Bereiche.',
    parentScopesNote:
      'Ein Elternteil erhält automatisch vollen Zugriff auf alle Bereiche.',
    sensitiveNote: 'Sensibel – wird nur ausdrücklich gewährt.',
    seesLabel: 'Sieht',
    seesNothing: 'Noch kein Zugriff auf einen Bereich.',
    fullAccess: 'Voller Zugriff',
    status: {
      invited: 'Eingeladen',
      active: 'Aktiv',
    },
    you: 'Sie',
    parentBadge: 'Elternteil',
    trustedBadge: 'Vertrauensperson',
    changeScopes: 'Zugriff bearbeiten',
    removeMember: 'Entfernen',
    removeConfirm: 'Diese Person wirklich aus der Familie entfernen?',
    onlyParents:
      'Nur Eltern können Familienmitglieder und deren Zugriff verwalten.',
  },

  scopes: {
    children: 'Kinder',
    calendar: 'Kalender',
    contacts: 'Kontakte',
    health: 'Gesundheit',
    documents: 'Dokumente',
    messages: 'Nachrichten',
    expenses: 'Kosten',
    shopping: 'Einkäufe',
  },

  mode: {
    /** Settings → "Režim rodiny" card. Parent-only, lives at the bottom. */
    cardTitle: 'Familienmodus',
    cardLead:
      'Solandio ist für eine Familie eingerichtet, die zusammenlebt. Wenn Sie in getrennten Haushalten leben, können Sie den Modus wechseln — alle Ihre Daten und die gesamte Geschichte der Kinder bleiben unberührt.',
    /** Current-mode labels. */
    familyLabel: 'Gemeinsamer Haushalt',
    familyDesc: 'Kalender, Kinder, Einkäufe und Kosten für die ganze Familie.',
    separatedLabel: 'Getrennte Haushalte',
    separatedDesc:
      'Fügt Wechselmodell, Kostenaufteilung, Vereinbarungen und Nachrichtenjournal hinzu.',
    currentLabel: 'Aktueller Modus',
    /** Switch actions. */
    switchToSeparated: 'Zu getrennten Haushalten wechseln',
    switchToFamily: 'Zurück zum gemeinsamen Haushalt wechseln',
    /** Two-step confirmation dialog (deliberate friction). */
    confirmToSeparatedTitle: 'In den Modus für getrennte Haushalte wechseln?',
    confirmToSeparatedLead:
      'Wir fügen Werkzeuge für getrennt lebende Eltern hinzu — Wechselmodell, Kostenaufteilung, Vereinbarungen und Nachrichtenjournal.',
    confirmToSeparatedNote:
      'Alle Ihre Daten und die gesamte Geschichte der Kinder bleiben unberührt. Sie können den Modus jederzeit wieder zurückwechseln.',
    confirmToSeparatedAction: 'Ja, wechseln',
    confirmToFamilyTitle: 'Zurück zum gemeinsamen Haushalt wechseln?',
    confirmToFamilyLead:
      'Die Werkzeuge für getrennte Haushalte werden ausgeblendet. Die Einträge bleiben gespeichert — beim nächsten Wechsel finden Sie sie genau so vor, wie sie waren.',
    confirmToFamilyAction: 'Ja, zurückwechseln',
    /** Shown while the change is being saved + after it lands. */
    switching: 'Modus wird gewechselt…',
    switched: 'Der Modus wurde geändert.',
    /** Gate copy when a non-parent somehow reaches the card. */
    onlyParents: 'Den Familienmodus können nur Eltern ändern.',
  },

  audit: {
    title: 'Protokoll',
    lead: 'Wer, was und wann geändert hat. Der Eintrag ist unveränderbar (nur anfügen).',
    empty: 'Noch keine Einträge.',
    onlyParents: 'Das Protokoll können nur Eltern einsehen.',
  },

  errors: {
    forbidden: 'Für diesen Bereich haben Sie keine Berechtigung.',
    notFound: 'Der Eintrag wurde nicht gefunden.',
    generic: 'Die Aktion ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
  },

  tour: {
    /** Re-invoke action (Settings + help button in the top bar). */
    start: 'Tour starten',
    help: 'Tour',
    helpHint: 'Spielen Sie die kurze App-Einführung jederzeit erneut ab.',
    /** Joyride control labels. */
    back: 'Zurück',
    next: 'Weiter',
    last: 'Fertig',
    skip: 'Überspringen',
    close: 'Schließen',
    /** "Krok 2 z 6" — {step} a {steps} doplní Joyride. */
    progress: 'Schritt {step} von {steps}',
    steps: {
      welcomeTitle: 'Willkommen bei Solandio',
      welcome: 'Wir zeigen Ihnen alles in wenigen Sekunden.',
      childrenTitle: 'Kinder',
      children: 'Hier fügen Sie Kinder hinzu und wechseln zwischen ihnen.',
      modulesTitle: 'Alles rund um die Familie',
      modules:
        'Links finden Sie alles für die Familie — Kalender, Kinder, Gesundheit, Einkäufe, Kosten, Dokumente und Nachrichten.',
      familyTitle: 'Familie',
      family:
        'Laden Sie hier den Partner oder die Großeltern ein und legen Sie fest, was sie sehen.',
      settingsTitle: 'Einstellungen',
      settings: 'In den Einstellungen ändern Sie Name und Sprache.',
      finishTitle: 'Fertig!',
      finish: 'Beginnen Sie mit einem Kind oder einer Einkaufsliste.',
    },
  },
};
